import { useState, useRef, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { formatDate, getChatTypeLabel, getStatusColor, getStatusLabel, cn } from '@/lib/utils'
import toast from 'react-hot-toast'
import { ArrowLeftIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline'

interface Message {
  _id: string
  sender: 'user' | 'ai' | 'teacher'
  content: string
  metadata?: {
    quizEvaluation?: {
      score: number
      passed: boolean
    }
  }
  createdAt: string
}

interface Chat {
  _id: string
  userId: {
    _id: string
    email: string
    name?: string
  }
  type: string
  status: string
  context?: {
    levelId?: {
      number: number
      titleRu: string
      quizQuestions?: any[]
    }
  }
}

export default function ChatDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const [message, setMessage] = useState('')
  
  const { data, isLoading } = useQuery({
    queryKey: ['chat', id],
    queryFn: async () => {
      const res = await api.get<{ data: { chat: Chat; messages: Message[] } }>(`/admin/chats/${id}`)
      return res.data.data
    },
    refetchInterval: 10000,
  })
  
  const sendMutation = useMutation({
    mutationFn: async (content: string) => {
      await api.post(`/admin/chats/${id}/message`, { content })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chat', id] })
      setMessage('')
    },
    onError: () => {
      toast.error('Ошибка отправки сообщения')
    },
  })
  
  const updateStatusMutation = useMutation({
    mutationFn: async (status: string) => {
      await api.patch(`/admin/chats/${id}/status`, { status })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chat', id] })
      toast.success('Статус обновлён')
    },
  })
  
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [data?.messages])
  
  const handleSend = (e: React.FormEvent) => {
    e.preventDefault()
    if (message.trim()) {
      sendMutation.mutate(message.trim())
    }
  }
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-400">Загрузка...</div>
      </div>
    )
  }
  
  if (!data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-400">Чат не найден</div>
      </div>
    )
  }
  
  const { chat, messages } = data
  
  return (
    <div className="flex flex-col h-[calc(100vh-6rem)] animate-fadeIn">
      {/* Header */}
      <div className="flex items-center justify-between pb-4 border-b border-gray-700">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/chats')}
            className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5" />
          </button>
          
          <div>
            <div className="flex items-center gap-2">
              <span className="font-medium text-white">
                {chat.userId?.email || 'Удалённый пользователь'}
              </span>
              <span className="badge-blue">{getChatTypeLabel(chat.type)}</span>
              <span className={getStatusColor(chat.status)}>
                {getStatusLabel(chat.status)}
              </span>
            </div>
            {chat.context?.levelId && (
              <p className="text-sm text-gray-400">
                Уровень {chat.context.levelId.number}: {chat.context.levelId.titleRu}
              </p>
            )}
          </div>
        </div>
        
        <div className="flex gap-2">
          <select
            value={chat.status}
            onChange={(e) => updateStatusMutation.mutate(e.target.value)}
            className="w-40"
          >
            <option value="active">Активный</option>
            <option value="resolved">Решён</option>
            <option value="archived">Архив</option>
          </select>
        </div>
      </div>
      
      {/* Messages */}
      <div className="flex-1 overflow-y-auto py-4 space-y-4">
        {messages.length === 0 ? (
          <div className="text-center text-gray-400 py-8">
            Сообщений пока нет
          </div>
        ) : (
          messages.map((msg) => (
            <div
              key={msg._id}
              className={cn(
                'flex',
                msg.sender === 'user' ? 'justify-start' : 'justify-end'
              )}
            >
              <div
                className={cn(
                  'max-w-[70%] rounded-2xl px-4 py-3',
                  msg.sender === 'user'
                    ? 'bg-gray-700 text-white rounded-bl-none'
                    : msg.sender === 'ai'
                    ? 'bg-purple-600/30 text-white rounded-br-none'
                    : 'bg-primary-600 text-white rounded-br-none'
                )}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-medium opacity-70">
                    {msg.sender === 'user' ? 'Пользователь' : msg.sender === 'ai' ? 'AI' : 'Преподаватель'}
                  </span>
                  <span className="text-xs opacity-50">
                    {formatDate(msg.createdAt, 'HH:mm')}
                  </span>
                </div>
                <p className="whitespace-pre-wrap">{msg.content}</p>
                
                {msg.metadata?.quizEvaluation && (
                  <div className={cn(
                    'mt-2 pt-2 border-t',
                    msg.metadata.quizEvaluation.passed
                      ? 'border-green-500/30'
                      : 'border-red-500/30'
                  )}>
                    <span className={cn(
                      'text-sm font-medium',
                      msg.metadata.quizEvaluation.passed ? 'text-green-400' : 'text-red-400'
                    )}>
                      Оценка: {msg.metadata.quizEvaluation.score}/100
                      {msg.metadata.quizEvaluation.passed ? ' ✓ Пройден' : ' ✗ Не пройден'}
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>
      
      {/* Input */}
      <form onSubmit={handleSend} className="pt-4 border-t border-gray-700">
        <div className="flex gap-3">
          <input
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Написать от имени преподавателя..."
            className="flex-1"
          />
          <button
            type="submit"
            disabled={!message.trim() || sendMutation.isPending}
            className="btn-primary"
          >
            <PaperAirplaneIcon className="w-5 h-5" />
          </button>
        </div>
      </form>
    </div>
  )
}
